import moment from "moment";
import { Pie } from "react-chartjs-2";
import { connect } from "react-redux";
import React, { useState, useEffect } from "react";
import { Box, Grid, Card, CircularProgress } from "@material-ui/core";

import toJs from "../../../hoc/toJS";
import select from "../../../utils/select";

// classify — phân loại 1 công bố đã accepted vào đúng 1 trong 7 nhóm.
// Refactor: "Hội thảo" giờ dựa thẳng vào `type` thay vì `conference.rank` —
// từ khi bỏ ô chọn hội thảo có sẵn (chỉ còn nhập tay), `conference` luôn null
// nên dựa vào rank sẽ khiến nhóm này vĩnh viễn = 0. Mọi trường hợp trước đây
// bị âm thầm bỏ qua (tạp chí không gán journal cụ thể, tạp chí trong nước,
// sách/chương sách/khác) giờ gom vào "Chưa xác định" thay vì biến mất khỏi
// biểu đồ — tổng hiển thị luôn khớp đúng tổng số công bố đã duyệt.
const classify = (scientificResearchElement) => {
  const sr = scientificResearchElement?.scientificResearch;
  const type = sr?.type;

  if (type === "scientificResearchConference" || type === "scientificResearchConferenceDomestic") {
    return "Conference";
  }
  if (type === "scientificResearchJournal") {
    if (scientificResearchElement?.journal?.coreCollection === "Scopus") return "Scopus";
    switch (scientificResearchElement?.journal?.sjrBestQuartile) {
      case "Q1":
        return "Q1";
      case "Q2":
        return "Q2";
      case "Q3":
        return "Q3";
      case "Q4":
        return "Q4";
      default:
        return "Unclassified";
    }
  }
  // Tạp chí trong nước, sách/chương sách, working, khác... — không có khái
  // niệm xếp hạng ISI/Scopus/quartile → gom chung "Chưa xác định".
  return "Unclassified";
};

// countByGroup — đếm số công bố theo từng nhóm cho 1 danh sách cho trước.
const countByGroup = (list) => {
  const counts = { Q1: 0, Q2: 0, Q3: 0, Q4: 0, Scopus: 0, Conference: 0, Unclassified: 0 };
  (list || []).forEach((item) => {
    if (!item?.scientificResearch?.accepted) return;
    counts[classify(item)] += 1;
  });
  return counts;
};

function ScientifiResearchCoreCollectionStatistic(props) {
  const [counts, setCounts] = useState(countByGroup([]));
  const [mainCounts, setMainCounts] = useState(countByGroup([]));

  const dateRangeLabel = () => {
    if (!props.chosenStartDate && !props.chosenEndDate) return "";
    const from = props.chosenStartDate ? moment(props.chosenStartDate).format("DD/MM/YYYY") : "";
    const to = props.chosenEndDate ? moment(props.chosenEndDate).format("DD/MM/YYYY") : "";
    if (from && to) return `từ ${from} đến ${to}`;
    if (from) return `từ ${from}`;
    return `đến ${to}`;
  };

  const pieOptions = {
    title: {
      display: true,
      fontSize: 13,
      fontColor: "#000000",
    },
    legend: { position: "left" },
  };

  const pieOptionsMain = {
    title: {
      display: true,
      fontSize: 13,
      fontColor: "#000000",
    },
    legend: { position: "left" },
  };

  useEffect(() => {
    if (!Array.isArray(props.scientificResearchs)) return;

    setCounts(countByGroup(props.scientificResearchs));

    const mainScientificResearchs = props.scientificResearchs.filter((item) =>
      (item?.scientificResearch?.scientificResearchProfile || []).some((author) => author.roleInPaper === "main")
    );
    setMainCounts(countByGroup(mainScientificResearchs));
  }, [props.scientificResearchs]);

  const { Q1, Q2, Q3, Q4, Scopus, Conference, Unclassified } = counts;
  const {
    Q1: mainQ1,
    Q2: mainQ2,
    Q3: mainQ3,
    Q4: mainQ4,
    Scopus: mainScopus,
    Conference: mainConference,
    Unclassified: mainUnclassified,
  } = mainCounts;

  const backgroundColor = [
    "#32936f", // green
    "#243474", // blue
    "#f0c808", // yellow
    "#81171b", // red
    "#f46423", // orange
    "#b5446e", // violet
    "#9e9e9e", // gray — Chưa xác định
  ];

  const labels = ["ISI-Q1", "ISI-Q2", "ISI-Q3", "ISI-Q4", "Scopus", "Hội thảo", "Chưa xác định"];

  const pieData = {
    labels,
    datasets: [
      {
        data: [Q1, Q2, Q3, Q4, Scopus, Conference, Unclassified],
        backgroundColor,
      },
    ],
  };

  const pieDataMain = {
    labels,
    datasets: [
      {
        data: [mainQ1, mainQ2, mainQ3, mainQ4, mainScopus, mainConference, mainUnclassified],
        backgroundColor,
      },
    ],
  };

  const total = Q1 + Q2 + Q3 + Q4 + Scopus + Conference + Unclassified;
  const mainTotal = mainQ1 + mainQ2 + mainQ3 + mainQ4 + mainScopus + mainConference + mainUnclassified;

  return (
    <Grid container style={{ height: "100%" }}>
      <Grid item xs={6} style={{ height: "100%" }}>
        <Card
          style={{
            height: "100%",
            width: "calc(100% - 20px)",
            padding: "0 8px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {props.isFetching ? (
            <CircularProgress />
          ) : (
            <>
              <Box height={24}>
                <b style={{ fontSize: 13, lineHeight: "24px" }}>
                  {`Phân loại công bố thuộc danh mục ISI/Scopus ${dateRangeLabel()}`}
                </b>
              </Box>
              <Box height="calc(100% - 48px)">
                <Pie data={pieData} options={pieOptions} />
              </Box>
              <Box style={{ textAlign: "center", fontSize: 13, lineHeight: "24px" }}>
                Tổng số công bố: {total}
              </Box>
            </>
          )}
        </Card>
      </Grid>
      <Grid item xs={6} style={{ height: "100%" }}>
        <Card
          style={{
            height: "100%",
            marginLeft: 4,
            width: "calc(100% - 20px)",
            padding: "0 8px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {props.isFetching ? (
            <CircularProgress />
          ) : (
            <>
              <Box height={24}>
                <b style={{ fontSize: 13 }}>
                  {`Phân loại công bố thuộc danh mục ISI/Scopus có tác giả chính ${dateRangeLabel()}`}
                </b>
              </Box>
              <Box height="calc(100% - 48px)">
                <Pie data={pieDataMain} options={pieOptionsMain} />
              </Box>
              <Box style={{ textAlign: "center", fontSize: 13, letterSpacing: -1, lineHeight: "24px" }}>
                Tổng số công bố có tác giả chính ở CMC: {mainTotal}
              </Box>
            </>
          )}
        </Card>
      </Grid>
    </Grid>
  );
}

const mapStateToProps = (state) => ({
  isFetching: select(state, "homeReducer", "isFetching"),
  scientificResearchs: select(state, "homeReducer", "dataScientificResearch"),
});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(toJs(ScientifiResearchCoreCollectionStatistic));
